import { SettingsApiTypes } from "@stellar/settings-api-client";

/** Removes column preferences that refer to columns no longer defined in the table */
export function pruneOrphanedColumns<K extends string>(
  preferences: SettingsApiTypes.ColumnPreferenceMap,
  validColumnsSet: Set<K>
): void {
  for (const key of Object.keys(preferences)) {
    if (!validColumnsSet.has(key as K)) {
      delete preferences[key];
    }
  }
}

/**
 * Computes the final list of visible columns in the correct order.
 * Combines data columns (that are visible) with always-visible columns appended last.
 */
export function getVisibleOrderedColumns<K extends string>(
  preferences: SettingsApiTypes.ColumnPreferenceMap | undefined,
  dataColumns: K[],
  alwaysVisibleColumns: K[],
  validColumnsSet: Set<K>,
  alwaysVisibleSet: Set<K>
): K[] {
  const baseVisibleColumns = preferences
    ? Object.entries(preferences)
        .filter(
          ([key, pref]) =>
            validColumnsSet.has(key as K) &&
            !alwaysVisibleSet.has(key as K) &&
            pref.visible
        )
        .sort(([, a], [, b]) => a.order - b.order)
        .map(([key]) => key as K)
    : dataColumns;

  return [...baseVisibleColumns, ...alwaysVisibleColumns];
}

/** Swaps the order of two columns in the preferences map, given the moved field and the target index */
export function swapColumnOrder(
  preferences: SettingsApiTypes.ColumnPreferenceMap,
  visibleFields: string[],
  oldIndex: number,
  targetIndex: number
): SettingsApiTypes.ColumnPreferenceMap {
  const updated: SettingsApiTypes.ColumnPreferenceMap = {};
  for (const key in preferences) {
    updated[key] = { ...preferences[key] };
  }

  const from = oldIndex - 1;
  const to = targetIndex - 1;
  const newOrder = [...visibleFields];
  newOrder.splice(to, 0, newOrder.splice(from, 1)[0]);

  newOrder.forEach((field, i) => {
    updated[field].order = i + 1;
    updated[field].visible = true;
  });

  return updated;
}

/**
 * Updates the column preferences map to reflect the current set of selected (visible) columns.
 *
 * - Sets the `visible` property to `true` for columns present in `selectedKeys`, and `false` otherwise.
 * - Preserves the existing `order` property for all columns.
 * - Ensures the "options" column (if present) is always visible.
 */
export function updatePreferencesWithOrder(
  columnPreferences: SettingsApiTypes.ColumnPreferenceMap,
  selectedKeys: Set<string>
): SettingsApiTypes.ColumnPreferenceMap {
  const updatedPreferences: SettingsApiTypes.ColumnPreferenceMap = {};

  for (const [key, pref] of Object.entries(columnPreferences)) {
    const willBeVisible = selectedKeys.has(key);
    updatedPreferences[key] = {
      ...pref,
      // eslint-disable-next-line @typescript-eslint/naming-convention
      visible: willBeVisible,
      order: pref.order,
    };
  }

  if (updatedPreferences.options) {
    updatedPreferences.options.visible = true;
  }

  return updatedPreferences;
}

/**
 * Normalizes the order of columns in the preferences map.
 *
 * - Assigns sequential order (1, 2, 3, ...) to all visible columns except those in alwaysVisibleColumns.
 * - Ensures all alwaysVisibleColumns (e.g. "options") that are visible are placed at the end, with the highest order values.
 * - Hidden columns retain their previous order value.
 */
export function normalizeColumnOrders(
  preferences: SettingsApiTypes.ColumnPreferenceMap,
  alwaysVisibleColumns: string[] = []
): SettingsApiTypes.ColumnPreferenceMap {
  const alwaysVisibleSet = new Set(alwaysVisibleColumns);
  const updated = { ...preferences };

  const visibleColumns: string[] = [];
  const hiddenColumns: string[] = [];

  Object.keys(updated).forEach((k) => {
    if (updated[k].visible && !alwaysVisibleSet.has(k)) {
      visibleColumns.push(k);
    } else if (!alwaysVisibleSet.has(k)) {
      hiddenColumns.push(k);
    }
  });

  visibleColumns.sort((a, b) => updated[a].order - updated[b].order);
  hiddenColumns.sort((a, b) => updated[a].order - updated[b].order);
  
  const all = [...visibleColumns, ...hiddenColumns, ...alwaysVisibleColumns];

  let order = 1;

  for (const k of all) {
    updated[k].order = order++;
    if (alwaysVisibleSet.has(k)) {
      updated[k].visible = true;
    }
  }

  return updated;
}
